import React from 'react';
import { Card, CardContent, Avatar, Box, Typography, Grid } from '@mui/material';
import { PhotographerBadge } from '../common/PhotographerBadge';

export interface PhotographerFeedCardData {
  userId: string;
  displayName: string;
  profilePhotoUrl?: string;
  photographerProfile?: any | null;
  photoCount: number;
  albumCount: number;
  recentPhotoUrls: string[];
  lastActivityAt?: Date | null;
}

interface PhotographerFeedCardProps {
  data: PhotographerFeedCardData;
  onOpenProfile: (userId: string) => void;
  onOpenPhoto?: (urls: string[], index: number) => void;
}

export const PhotographerFeedCard: React.FC<PhotographerFeedCardProps> = ({
  data,
  onOpenProfile,
  onOpenPhoto,
}) => {
  const { userId, displayName, profilePhotoUrl, photographerProfile, photoCount, albumCount, recentPhotoUrls } =
    data;

  const hasPhotos = recentPhotoUrls && recentPhotoUrls.length > 0;

  const handleCardClick = () => {
    onOpenProfile(userId);
  };

  const handlePhotoClick = (index: number) => {
    if (onOpenPhoto && hasPhotos) {
      onOpenPhoto(recentPhotoUrls, index);
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        border: '1px solid rgba(255,255,255,0.06)',
        background:
          'linear-gradient(145deg, rgba(9,10,15,0.98), rgba(10,16,24,0.98))',
        overflow: 'hidden',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 6px 18px rgba(0,0,0,0.5)',
          transform: 'translateY(-1px)',
          transition: 'all 0.15s ease-in-out',
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: 2.25 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1.5,
            gap: 1.5,
          }}
        >
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={profilePhotoUrl}
              sx={{
                width: 44,
                height: 44,
                border: '2px solid rgba(255,255,255,0.9)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                bgcolor: 'rgba(255,255,255,0.08)',
              }}
            >
              {displayName?.charAt(0) || '?'}
            </Avatar>
            <PhotographerBadge variant="avatarOverlay" size="sm" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'rgba(255,255,255,0.95)',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
              title={displayName}
            >
              {displayName || photographerProfile?.name || 'Photographer'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.6)' }}
            >
              {photoCount} photo{photoCount === 1 ? '' : 's'} · {albumCount}{' '}
              album{albumCount === 1 ? '' : 's'}
            </Typography>
          </Box>
        </Box>

        {hasPhotos && (
          <Grid container spacing={0.5} sx={{ mt: 0.5 }}>
            {recentPhotoUrls.slice(0, 6).map((url, index) => (
              <Grid item xs={4} key={url + index}>
                <Box
                  sx={{
                    position: 'relative',
                    pt: '100%',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    cursor: onOpenPhoto ? 'pointer' : 'default',
                    '&:hover img': {
                      transform: 'scale(1.05)',
                    },
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handlePhotoClick(index);
                  }}
                >
                  <Box
                    component="img"
                    src={url}
                    alt={`Photo ${index + 1}`}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.2s ease-out',
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default PhotographerFeedCard;

